import {
  getOperationProgressGrpc,
  readPendingRevitOperation,
  setPendingRevitOperation,
  type PendingRevitOperationV2,
  type RevitOperationProgressSnapshot,
} from '../api/grpc/operationProgressClient';
import {
  E2E_ACTIVE_DOCUMENT_NAME,
  E2E_ACTIVE_DOCUMENT_PATH,
  resolveActiveProjectKey,
} from '../hooks/useActiveDocumentName';
import { getE2eBootstrapConfig } from './e2eBootstrap';

/**
 * Screenshot seam for the mismatched-pending-recovery state. The mock
 * Playwright lane has no Revit, so a real pending record would classify as
 * lost on the first probe and the blocked UI would vanish before a screenshot.
 * With `e2ePendingConnectorOperation=family-type` active, a Family-type v2
 * record is seeded once (never clobbering a real one) and progress probes for
 * its operation id report a permanently live operation, keeping the blocked
 * mode-mismatch UI stable. Every other operation id still hits the real
 * transport.
 */
export const E2E_PENDING_CONNECTOR_OPERATION_ID = 'e2e-pending-connector-family-type';

type OperationProgressFetch = typeof getOperationProgressGrpc;

function isPendingConnectorFixtureActive(search: string): boolean {
  return getE2eBootstrapConfig(search)?.pendingConnectorOperation != null;
}

export function buildE2ePendingConnectorOperationRecord(): PendingRevitOperationV2 | null {
  const projectKey = resolveActiveProjectKey({
    documentName: E2E_ACTIVE_DOCUMENT_NAME,
    documentPath: E2E_ACTIVE_DOCUMENT_PATH,
  });
  if (!projectKey) {
    return null;
  }

  return {
    version: 2,
    operationId: E2E_PENDING_CONNECTOR_OPERATION_ID,
    operationType: 'parameters.family.add',
    owner: 'connector-parameters',
    title: 'Adding connector parameters',
    startedAt: Date.now(),
    projectKey,
    expectedDocumentName: E2E_ACTIVE_DOCUMENT_NAME,
    expectedDocumentPath: E2E_ACTIVE_DOCUMENT_PATH,
    connectorTarget: { destination: 'family', bindingKind: 'type' },
  };
}

export function ensureE2ePendingConnectorOperationSeeded(
  search: string = window.location.search
): void {
  if (!isPendingConnectorFixtureActive(search)) {
    return;
  }
  if (readPendingRevitOperation().kind !== 'none') {
    return;
  }

  const record = buildE2ePendingConnectorOperationRecord();
  if (record) {
    setPendingRevitOperation(record);
  }
}

function buildLiveFixtureProgressSnapshot(): RevitOperationProgressSnapshot {
  return {
    operationId: E2E_PENDING_CONNECTOR_OPERATION_ID,
    operationType: 'parameters.family.add',
    phase: 'running',
    state: 'running',
    functionName: '',
    message: 'Revit is applying connector parameter setup',
    resultReady: false,
    failed: false,
    items: [],
  };
}

export function resolveOperationProgressFetch(
  search: string = window.location.search
): OperationProgressFetch {
  if (!isPendingConnectorFixtureActive(search)) {
    return getOperationProgressGrpc;
  }

  return async (operationId: string) => {
    if (operationId !== E2E_PENDING_CONNECTOR_OPERATION_ID) {
      return getOperationProgressGrpc(operationId);
    }
    return { success: true, progress: buildLiveFixtureProgressSnapshot() };
  };
}
